import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

import * as S from './styled';
import { BatteryAnimation, CopyIcon, ErrorIcon, PlayIcon, ReadyIcon, StopIcon } from '../../Icons';
import bg from '../../../assets/img/cardItemImg.png';
import station from '../../../assets/img/stationImg.png';
import { OfflineIcon } from '../../Icons/OfflineIcon';
import CommonModal from '../CommonModal';
import AvailableUsers from '../AvailableUsers';
import WarningModal from '../WarningModal';
import BatteryPreparingAnimated from '../../AnimatedIcons/battery-preparing-animated';
import ActionsWithUser from '../ActionsWithUser';
import ChartForCard from '../ChartForCard/ChartForCard';
import { CHARGING_SESSION_STATE, CHARGING_STATUS_TYPES } from '../../../enums';
import { useContextInfo } from '../../../context';
import { CircularProgress } from '@mui/material';
import { charge_amount_formating, cost_formating } from '../../../utils/custom_formating';
import { useQuery } from '@apollo/client';
import { GET_LATES_CHARGINSESSION } from '../../../apollo/chargingHistory';
import { LockOpen } from '@mui/icons-material';
import DurationDisplay from '../DurationDisplay';


const CardItem = ({
  value,
  startChargingProcess,
  stopChargingProcess,
  siteValue,
  loading,
  showErrorModal,
  handlerOpenEditModal,
  unlockConnectorHandler
}) => {
  const { t } = useTranslation();
  const { userInfo, isAdminForCurrentSite } = useContextInfo();
  const { info } = userInfo;

  const [waiting, setWaiting] = useState(false);
  const [connectorValue, setConnectorValue] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);
  const [isOpenActionsModal, setIsOpenActionsModal] = useState(false);

  const [now, setNow] = useState(moment());

  useEffect(() => {
    // Aktualisiert 'now' jede Sekunde (1000ms)
    const intervalId = setInterval(() => {
      setNow(moment());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const {
    data: latest_chargingsession,
    loading: get_session_loading,
    refetch
  } = useQuery(GET_LATES_CHARGINSESSION, {
    variables: {
      filter: {
        evseId: value.evseId,
        state: CHARGING_SESSION_STATE.ACTIVE,
        siteId: siteValue?.id
      }
    },
    fetchPolicy: 'no-cache'
  });

  const statusInfo = value?.connectorStates?.[0];
  //const newestChargingSession = latest_chargingsession?.charging_latest_session_for_evse_id;
  const chargingInfo = statusInfo && JSON.parse(statusInfo?.message);
  const [newestChargingSession, setNewestChargingSession] = useState(latest_chargingsession?.charging_latest_session_for_evse_id);

  useEffect(() => {
    refetch({
      filter: {
        evseId: value.evseId,
        state: CHARGING_SESSION_STATE.ACTIVE,
        siteId: siteValue?.id
      }
    }).then((response) => {
      setNewestChargingSession(response?.data?.charging_latest_session_for_evse_id);
    });
  }, [value]);


  const handlerChooseUser = async (user = null) => {
    await startChargingProcess(value, user);
    closeModal();
    setWaiting(true);
    setTimeout(() => setWaiting(false), 10000);
  };

  const handlerStartProcess = () => {
    if (isAdminForCurrentSite) {
      setIsOpenModal(true);
    } else {
      handlerChooseUser();
    }
  };

  const handlerOpenActionsModal = () => {
    setIsOpenActionsModal(true);
    setConnectorValue(value);
  };

  const closeModal = () => setIsOpenModal(false);

  const closeWarningModal = () => setIsOpenWarningModal(false);

  const handlerStopProcess = async () => {
    await stopChargingProcess(value);
    closeWarningModal();
    setWaiting(true);
    setTimeout(() => setWaiting(false), 10000);
  };

  const handlerShowErrorModal = () => showErrorModal(value?.connectorStates?.[0]?.message);

  const setStatus = useMemo(() => {
    switch (statusInfo?.state) {
      case CHARGING_STATUS_TYPES.READY: {
        return (
          <>
            <S.StatusBlock>
              <ReadyIcon />
              <S.StatusText>{t('overviewWallboxes.status_ready')}</S.StatusText>
            </S.StatusBlock>

            <S.ActionBlock onClick={handlerStartProcess}>
              <PlayIcon />
            </S.ActionBlock>
          </>
        );
      }

      case CHARGING_STATUS_TYPES.SUSPENDED:
      case CHARGING_STATUS_TYPES.SUSPENDED_ENV: {
          if (
            newestChargingSession &&
            newestChargingSession?.user_id !== info.id &&
            !isAdminForCurrentSite
          ) {
            return (
              <>
                <S.StatusBlockOccupied>
                  <BatteryAnimation />
                  <S.StatusTextOccupied>{t('overviewWallboxes.occupied')}</S.StatusTextOccupied>
                </S.StatusBlockOccupied>
              </>
            );
          } else {
            return (
              <>
                <S.StatusBlock>
                  <BatteryPreparingAnimated />
                  <S.StatusText>{t('overviewWallboxes.suspended')}</S.StatusText>
                </S.StatusBlock>

                <S.ActionBlock onClick={handlerStartProcess}>
                  <PlayIcon />
                </S.ActionBlock>
              </>
            );
          }
      }

      case CHARGING_STATUS_TYPES.PREPARING: {
        return (
          <>
            <S.StatusBlock>
              <BatteryPreparingAnimated />
              <S.StatusText>{t('overviewWallboxes.preparing')}</S.StatusText>
            </S.StatusBlock>

            <S.ActionBlock onClick={handlerStartProcess}>
              <PlayIcon />
            </S.ActionBlock>
          </>
        );
      }

      case CHARGING_STATUS_TYPES.CHARGING : {
        if (
          newestChargingSession &&
          newestChargingSession?.user_id !== info.id &&
          !isAdminForCurrentSite
        ) {
          return (
            <>
              <S.StatusBlockOccupied>
                <BatteryAnimation />
                <S.StatusTextOccupied>{t('overviewWallboxes.occupied')}</S.StatusTextOccupied>
              </S.StatusBlockOccupied>
            </>
          );
        } else {
          return (
            <>
              <S.StatusBlock>
                <BatteryAnimation />
                <S.StatusText>{t('overviewWallboxes.charging')}</S.StatusText>
              </S.StatusBlock>
              <S.ActionBlock charging={true} onClick={() => setIsOpenWarningModal(true)}>
                <StopIcon />
              </S.ActionBlock>
            </>
          );
        }

      }

      case CHARGING_STATUS_TYPES.ERROR: {
        return (
          <>
            <S.StatusBlock>
              <ErrorIcon />
              <S.StatusText>{t('overviewWallboxes.error')}</S.StatusText>
            </S.StatusBlock>

            <S.ActionBlock charging={true} onClick={handlerShowErrorModal}>
              <PriorityHighIcon style={{ fill: '#FB3E3F', width: '100%', height: '50px' }} />
            </S.ActionBlock>
          </>
        );
      }

      case CHARGING_STATUS_TYPES.OFFLINE: {
        return (
          <>
            <S.StatusBlock>
              <OfflineIcon />
              <S.StatusText>{t('overviewWallboxes.offline')}</S.StatusText>
              <S.OfflineLastDateText>{JSON.parse(statusInfo?.message)?.msg}</S.OfflineLastDateText>
            </S.StatusBlock>
          </>
        );
      }

      case CHARGING_STATUS_TYPES.NO_CONNECTION:
      case CHARGING_STATUS_TYPES.UNAVAILABLE: {
        return (
          <>
            <S.StatusBlock>
              <OfflineIcon />
              <S.StatusText>{t('overviewWallboxes.no_connection')}</S.StatusText>
              <S.OfflineLastDateText>{JSON.parse(statusInfo?.message)?.msg}</S.OfflineLastDateText>
            </S.StatusBlock>
          </>
        );
      }

      default:
        return (
          <>
            <S.StatusBlock>
              <ErrorIcon />
              <S.StatusText>{t('overviewWallboxes.error')}</S.StatusText>
            </S.StatusBlock>

            <S.ActionBlock charging={true} onClick={handlerShowErrorModal}>
              <PriorityHighIcon style={{ fill: '#FB3E3F', width: '100%', height: '50px' }} />
            </S.ActionBlock>
          </>
        );
    }
  }, [statusInfo, get_session_loading, newestChargingSession]);

  const setStatusWaiting = useMemo(() => {
    return (
      <>
        <S.StatusBlock>
          <CircularProgress />
          <S.StatusText>{t('overviewWallboxes.waiting')}</S.StatusText>
        </S.StatusBlock>
      </>
    );
  }, [waiting, get_session_loading]);

  const closeActionsModal = () => setIsOpenActionsModal(false);

  return (
    <S.CardWrapper>
      <CommonModal open={isOpenModal} onClose={closeModal}>
        <AvailableUsers
          onClose={closeModal}
          handlerChooseUser={handlerChooseUser}
          title={value?.name}
          users={value?.contracts}
          evseId={value?.evseId}
          siteId={siteValue?.id}
        />
      </CommonModal>

      <CommonModal open={isOpenWarningModal} onClose={closeWarningModal}>
        <WarningModal
          onClose={closeWarningModal}
          handlerProcessedClick={handlerStopProcess}
          description={''}
        />
      </CommonModal>

      <CommonModal open={isOpenActionsModal} onClose={closeActionsModal}>
        <ActionsWithUser
          handlerCloseModal={closeActionsModal}
          connectorValue={connectorValue}
          activeUsers={value?.contracts}
          activeGroups={value?.groups}
          siteValue={siteValue}
        />
      </CommonModal>

      <S.ImageBlock
        image={bg}
        error={
          statusInfo?.state === CHARGING_STATUS_TYPES.ERROR ||
          statusInfo?.state === CHARGING_STATUS_TYPES.OFFLINE
        }>
        <S.StationImg src={station} alt="station" />
        {waiting || loading || get_session_loading ? setStatusWaiting : setStatus}
      </S.ImageBlock>

      <S.InfoBlock>
        <S.InfoHeader>
          <div>
            <S.InfoHeaderTitle>
              {value?.name}
              {isAdminForCurrentSite && (
                <span style={{ marginLeft: '8px' }}>
                  <CopyIcon
                    width={'13px'}
                    height={'13px'}
                    onClick={() => handlerOpenEditModal(value)}
                  />
                </span>
              )}
            </S.InfoHeaderTitle>
            <S.InfoHeaderSubtitle>
              {value?.evseId}
              {isAdminForCurrentSite && (
                <span style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <LockOpen
                    fontSize="small"
                    onClick={() => unlockConnectorHandler(value?.evseId)}
                  />
                </span>
              )}
            </S.InfoHeaderSubtitle>
          </div>

          {isAdminForCurrentSite &&
            (statusInfo?.state === CHARGING_STATUS_TYPES.SUSPENDED_ENV ||
              statusInfo?.state === CHARGING_STATUS_TYPES.SUSPENDED ||
              statusInfo?.state === CHARGING_STATUS_TYPES.CHARGING ||
              statusInfo?.state === CHARGING_STATUS_TYPES.PREPARING) && (
              <S.ChargingUser>
                <S.ChargingUserItem>{newestChargingSession?.contract_name}</S.ChargingUserItem>
              </S.ChargingUser>
            )}
        </S.InfoHeader>

        {statusInfo?.state === CHARGING_STATUS_TYPES.PREPARING ||
        (statusInfo?.state === CHARGING_STATUS_TYPES.CHARGING &&
          (isAdminForCurrentSite || newestChargingSession?.user_id === info.id)) ? (
          <>
            <S.Content>
              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.start')}</S.ContentItemTitle>
                <S.ContentItemData>
                  {newestChargingSession?.startTime
                    ? moment(newestChargingSession?.startTime).format('YYYY-MM-DD HH:mm')
                    : '-'}
                </S.ContentItemData>
              </S.ContentItem>

              <S.ContentBorder />

              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.duration')}</S.ContentItemTitle>
                {newestChargingSession?.startTime ? (
                  <DurationDisplay
                    startTime={moment(newestChargingSession?.startTime)}
                    currentTime={now}
                  />
                ) : (
                  '-'
                )}
              </S.ContentItem>
            </S.Content>

            <S.Content>
              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.power_consumption')}</S.ContentItemTitle>
                <S.ContentItemData>
                  {chargingInfo?.amount ? charge_amount_formating(chargingInfo?.amount) : 0} kWh
                </S.ContentItemData>
              </S.ContentItem>

              <S.ContentBorder />

              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.costs')}</S.ContentItemTitle>
                <S.ContentItemData>
                  €{chargingInfo?.cost ? cost_formating(chargingInfo?.cost) : 0}
                </S.ContentItemData>
              </S.ContentItem>
            </S.Content>
          </>
        ) : (statusInfo?.state === CHARGING_STATUS_TYPES.SUSPENDED ||
            statusInfo?.state === CHARGING_STATUS_TYPES.SUSPENDED_ENV) &&
          (isAdminForCurrentSite || newestChargingSession?.user_id === info.id) ? (
          <>
            <S.Content>
              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.start')}</S.ContentItemTitle>
                <S.ContentItemData>
                  {newestChargingSession?.startTime
                    ? moment(newestChargingSession?.startTime).format('YYYY-MM-DD HH:mm')
                    : '-'}
                </S.ContentItemData>
              </S.ContentItem>
              <S.ContentBorder />
              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.duration')}</S.ContentItemTitle>
                {newestChargingSession?.startTime ? (
                  <DurationDisplay
                    startTime={moment(newestChargingSession?.startTime)}
                    currentTime={now}
                  />
                ) : (
                  '-'
                )}
              </S.ContentItem>
            </S.Content>

            <S.Content>
              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.power_consumption')}</S.ContentItemTitle>
                <S.ContentItemData>
                  {chargingInfo?.last_charging_details?.amount
                    ? charge_amount_formating(chargingInfo?.last_charging_details?.amount)
                    : 0}{' '}
                  kWh
                </S.ContentItemData>
              </S.ContentItem>

              <S.ContentBorder />

              <S.ContentItem>
                <S.ContentItemTitle>{t('overviewWallboxes.costs')}</S.ContentItemTitle>
                <S.ContentItemData>
                  €
                  {chargingInfo?.last_charging_details?.cost
                    ? cost_formating(chargingInfo?.last_charging_details?.cost)
                    : 0}
                </S.ContentItemData>
              </S.ContentItem>
            </S.Content>
          </>
        ) : (
          <ChartForCard evseId={value?.evseId} siteId={siteValue?.id} />
        )}

        {isAdminForCurrentSite && (
          <>
            <S.UserBlockTitle>{t('overviewWallboxes.manage_users')}</S.UserBlockTitle>
            <S.UserBlock>
              {value?.groups?.length
                ? value?.groups
                    ?.slice()
                    .sort((a, b) => a.group_name.localeCompare(b.group_name))
                    .map((item) => (
                      <S.GroupItem key={item?.group_id}>{item?.group_name}</S.GroupItem>
                    ))
                : null}
              {value?.contracts?.length
                ? value?.contracts
                    ?.slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((item) => <S.UserItem key={item?.evcoId}>{item?.name}</S.UserItem>)
                : null}
              <S.UserCopyIcon>
                <CopyIcon width={14} height={14} onClick={handlerOpenActionsModal} />
              </S.UserCopyIcon>
            </S.UserBlock>
          </>
        )}
      </S.InfoBlock>
    </S.CardWrapper>
  );
};

export default CardItem;
